import React, { useState, useEffect } from 'react';
import {
  fetchShipmentsForAutoInvoicing,
  generateInvoicePDF,
  createInvoice,
  fetchClientByCode,
} from '../../services/api';
import LoadingButton from '../LoadingButton/LoadingButton';
import EmailSelector from '../EmailSelector/EmailSelector';
import MessageBox from '../MessageBox/MessageBox';
import './autoInvoicingComponent.css';

export default function AutoInvoicingComponent({ onClose, onCancel }) {
  const [shipments, setShipments] = useState([]);
  const selectedShipments = shipments.filter(shipment => shipment.selected);
  const [queue, setQueue] = useState([]);
  const [currentShipment, setCurrentShipment] = useState(null);
  const [showMailSelector, setShowMailSelector] = useState(false);
  const [showMessageBox, setShowMessageBox] = useState(false);
  const [messageBoxConfig, setMessageBoxConfig] = useState({
    message: '',
    type: 'ok',
    onConfirm: null,
    onCancel: null,
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const fetchedData = await fetchShipmentsForAutoInvoicing();
        if (fetchedData) {
          const extractedData = fetchedData.map(shipment => ({
            ...shipment,
            selected: false,
          }));
          setShipments(extractedData);
        } else {
          setShipments([]);
          console.error('La réponse ne contient pas de données');
        }
      } catch (error) {
        console.error('Erreur lors de la récupération des données:', error);
        setShipments([]);
      }
    };

    fetchData();
  }, []);

  const handleSelectAll = () => {
    setShipments(shipments.map(shipment => ({ ...shipment, selected: true })));
  };

  const handleDeselectAll = () => {
    setShipments(shipments.map(shipment => ({ ...shipment, selected: false })));
  };

  const handleToggleSelect = index => {
    const newShipments = [...shipments];
    newShipments[index].selected = !newShipments[index].selected;
    setShipments(newShipments);
  };

  const onTriggerConfirm = async () => {
    setQueue(selectedShipments);
    await processNextShipment(true);
  };

  const processNextShipment = async (first = false) => {
    if (queue.length === 0) {
      if (!first) {
        onClose();
      }
      return;
    }

    const [nextShipment, ...remainingQueue] = queue;
    setQueue(remainingQueue);
    setCurrentShipment(nextShipment);
    await startProcessing(nextShipment);
  };

  const startProcessing = async shipment => {
    const fetchInvoicePDF = async shipment => {
      try {
        const response = await generateInvoicePDF(shipment.client, [shipment], 'invoice');
        return response;
      } catch (error) {
        setMessageBoxConfig({
          message: `Une erreur est survenue lors de la génération du pdf pour le dossier ${shipment.shipment_number}: ${error}. Passage au dossier suivant.`,
          type: 'ok',
          onConfirm: () => {
            setShowMessageBox(false);
            processNextShipment();
          },
          onClose: () => {
            setShowMessageBox(false);
            processNextShipment();
          },
        });
        setShowMessageBox(true);
        return null;
      }
    };
    const response = await fetchInvoicePDF(shipment);
    if (response) {
      setShowMailSelector(true);
    }
  };

  const handleConfirmEmailSelector = async email_list => {
    const CreateInvoice = async (shipment, email_list) => {
      try {
        const response = await createInvoice(
          shipment.client,
          [shipment],
          'invoice',
          email_list,
          '',
          ''
        );
        return response;
      } catch (error) {
        setMessageBoxConfig({
          message: `Une erreur est survenue lors de la création de la facture pour le dossier ${shipment.shipment_number}: ${error}. Passage au dossier suivant.`,
          type: 'ok',
          onConfirm: () => {
            setShowMessageBox(false);
            processNextShipment();
          },
          onClose: () => {
            setShowMessageBox(false);
            processNextShipment();
          },
        });
        setShowMessageBox(true);
        return null;
      }
    };

    const response = await CreateInvoice(currentShipment, email_list);
    setShowMailSelector(false);

    if (response) {
      const { email_sent } = response;

      if (email_sent) {
        processNextShipment();
      } else {
        setMessageBoxConfig({
          message: `L'envoi de l'email a rencontré une erreur, la facture n'a pas pu être créée pour le dossier ${currentShipment.shipment_number}. Passage au dossier suivant.`,
          type: 'ok',
          onConfirm: () => {
            setShowMessageBox(false);
            processNextShipment();
          },
          onClose: () => {
            setShowMessageBox(false);
            processNextShipment();
          },
        });
        setShowMessageBox(true);
      }
    }
  };

  return (
    <div className="overlay file-list-overlay">
      <div className="auto-invoicing-component-content">
        <div className="shipment-selector-grid">
          <span className="overlay-close" onClick={onClose}>
            &times;
          </span>
          <p>Ces dossiers peuvent être facturés aujourd'hui:</p>
          <div className="shipments-list">
            <table>
              <tbody>
                {shipments.map((shipment, index) => (
                  <tr key={index}>
                    <td>
                      <input
                        type="checkbox"
                        checked={shipment.selected}
                        onChange={() => handleToggleSelect(index)}
                      />
                    </td>
                    <td onClick={() => handleToggleSelect(index)}>{shipment.shipment_number}</td>
                    <td
                      className="auto-invoicing-client-cell"
                      title={shipment.client}
                      onClick={() => handleToggleSelect(index)}
                    >
                      {shipment.client}
                    </td>
                    <td onClick={() => handleToggleSelect(index)}>
                      {`Liv: ${shipment.delivery_date}`}
                    </td>
                    <td onClick={() => handleToggleSelect(index)}>{`${shipment.client_price}€`}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="select-all-buttons auto-invoicing">
            <button type="button" onClick={handleSelectAll}>
              Tout sélectionner
            </button>
            <button type="button" onClick={handleDeselectAll}>
              Tout désélectionner
            </button>
          </div>
          <div className="shipment-selector-buttons">
            <LoadingButton onClick={onTriggerConfirm}>Valider</LoadingButton>
            <button type="button" onClick={onCancel}>
              Annuler
            </button>
          </div>
        </div>
      </div>

      {showMailSelector && (
        <EmailSelector
          fetchFunction={fetchClientByCode}
          code={currentShipment.client}
          onClose={() => setShowMailSelector(false)}
          onConfirm={handleConfirmEmailSelector}
          onCancel={() => setShowMailSelector(false)}
        />
      )}

      {showMessageBox && (
        <MessageBox
          message={messageBoxConfig.message}
          onClose={() => setShowMessageBox(false)}
          onConfirm={messageBoxConfig.onConfirm}
          onCancel={messageBoxConfig.onCancel}
          type={messageBoxConfig.type}
          technicalDetails={messageBoxConfig.technicalDetails}
        />
      )}
    </div>
  );
}
